/** @jsxImportSource @emotion/react */
import React, { useCallback, useState } from 'react'
import Typography from '@mui/material/Typography'
import FormHelperText from '@mui/material/FormHelperText'
import CustomEditor from 'ckeditor5-custom-build'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import * as classes from './styles'

const TABLE_OF_CONTENT_PLUGIN = 'tableOfContent'
const TOGGLE_BAR_CLASS_PLUGIN = 'toggleBarClass'
const INSERT_PLATFORM_TEMPLATE_PLUGIN = 'insertPlatformTemplate'
const PREVIEW_IMAGE_PLUGIN = 'previewImage'

export type EditorType = 'default' | 'passage' | 'question' | 'option' | 'solution' | 'extensionSolution' | 'article'

/**
 * Optional toolbar items from the shared master list in ckeditor/src/ckeditor.ts.
 * Types only list what they keep; everything else here is removed.
 */
const OPTIONAL_TOOLBAR_ITEMS = [
  TABLE_OF_CONTENT_PLUGIN,
  TOGGLE_BAR_CLASS_PLUGIN,
  INSERT_PLATFORM_TEMPLATE_PLUGIN,
  'blockQuote',
  'insertTable',
  'mediaEmbed',
  'imageInsert',
  PREVIEW_IMAGE_PLUGIN,
  'MathType',
  'ChemType',
] as const

type OptionalToolbarItem = (typeof OPTIONAL_TOOLBAR_ITEMS)[number]

const OPTIONAL_ITEMS_BY_TYPE: Record<EditorType, readonly OptionalToolbarItem[]> = {
  // simple editor — font styles/lists/link ok; no quotes/tables/images/videos/formulas/custom plugins
  default: [],
  // imageInsert
  passage: ['imageInsert'],
  // insertTable, imageInsert, MathType, ChemType
  question: ['insertTable', 'imageInsert', 'MathType', 'ChemType'],
  // imageInsert, MathType, ChemType
  option: ['imageInsert', 'MathType', 'ChemType'],
  // toggleBar, insertPlatform, blockQuote, insertTable, mediaEmbed, imageInsert, previewImage, MathType, ChemType
  solution: [
    TOGGLE_BAR_CLASS_PLUGIN,
    INSERT_PLATFORM_TEMPLATE_PLUGIN,
    'blockQuote',
    'insertTable',
    'mediaEmbed',
    'imageInsert',
    PREVIEW_IMAGE_PLUGIN,
    'MathType',
    'ChemType',
  ],
  // toggleBar, blockQuote, insertTable, mediaEmbed, imageInsert, MathType, ChemType
  extensionSolution: [
    TOGGLE_BAR_CLASS_PLUGIN,
    'blockQuote',
    'insertTable',
    'mediaEmbed',
    'imageInsert',
    'MathType',
    'ChemType',
  ],
  // tableOfContent, blockQuote, insertTable, mediaEmbed, imageInsert, MathType, ChemType
  article: [TABLE_OF_CONTENT_PLUGIN, 'blockQuote', 'insertTable', 'mediaEmbed', 'imageInsert', 'MathType', 'ChemType'],
}

/** Custom plugins whose toolbar item name matches the plugin name — safe for removePlugins. */
const REMOVABLE_PLUGINS = [
  TABLE_OF_CONTENT_PLUGIN,
  TOGGLE_BAR_CLASS_PLUGIN,
  INSERT_PLATFORM_TEMPLATE_PLUGIN,
  PREVIEW_IMAGE_PLUGIN,
  'MathType',
] as const

const getHiddenToolbarItems = (editorType: EditorType): string[] => {
  const allowed = new Set<string>(OPTIONAL_ITEMS_BY_TYPE[editorType])

  return OPTIONAL_TOOLBAR_ITEMS.filter((item) => !allowed.has(item))
}

const getRemovePlugins = (hiddenToolbarItems: string[]): string[] =>
  REMOVABLE_PLUGINS.filter((pluginName) => hiddenToolbarItems.includes(pluginName))

export type EditorProps = {
  type?: EditorType
  value: string // HTML-string
  onChange: (value: string) => void
  onFocus?: () => void
  onBlur?: () => void
  label?: string
  placeholder?: string
  formHelperText?: string
  error?: boolean
  disabled?: boolean
  hiddenInputName?: string
  className?: string
}

// TODO: fix z-index for tooltip/dropdown in a modal:
// override CKEditor global styles in src/index.css:
// :root {
//   --ck-z-default: 1400 !important;
// }
// but a click on the ckeditor-tooltip works like a click outside the main modal and nothing works
// UPD: it may be fixed with next Modal props: disableEnforceFocus and sx={{ zIndex: 100 }}
export const Editor = ({
  type = 'default',
  value,
  onChange,
  onFocus,
  onBlur,
  label,
  placeholder,
  formHelperText,
  error,
  disabled,
  hiddenInputName,
  className,
}: EditorProps) => {
  const [id] = useState('editor-' + String(Math.random()).replace('.', ''))
  const [focus, setFocus] = useState(false)

  const hiddenToolbarItems = getHiddenToolbarItems(type)
  const removePlugins = getRemovePlugins(hiddenToolbarItems)
  const withTableOfContent = type === 'article'

  const handleFocus = useCallback(() => {
    setFocus(true)
    onFocus?.()
  }, [onFocus])

  const handleBlur = useCallback(() => {
    setFocus(false)
    onBlur?.()
  }, [onBlur])

  const handleFocusOnEditor = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    document.getElementById(id)?.querySelector('.ck-content')?.focus?.()
  }, [id])

  return (
    <div>
      {!!label && (
        <Typography css={classes.label} onClick={handleFocusOnEditor}>
          {label}
        </Typography>
      )}
      <input name={hiddenInputName} onFocus={handleFocusOnEditor} css={classes.hiddenInput} />
      <div id={id} className={`text-editor ${className}`} css={classes.editor({ focus, error, withTableOfContent })}>
        <CKEditor
          editor={CustomEditor}
          data={value ?? ''}
          onChange={(event, editor) => {
            const data = editor.getData()
            onChange(data)
          }}
          onBlur={handleBlur}
          onFocus={handleFocus}
          disabled={disabled}
          config={{
            placeholder,
            removePlugins,
            toolbar: {
              removeItems: hiddenToolbarItems,
            },
          }}
        />
      </div>
      {!!formHelperText && <FormHelperText error={error}>{formHelperText}</FormHelperText>}
    </div>
  )
}

export default Editor
